// ─────────────────────────────────────────────────────────────────────────────
// useNodeLogs — live log tail for one node, over SSE.
//
//   GET /enterprise-nodes/:id/logs/stream?token=…   (text/event-stream)
//
// This only started working with the move off /nodes: that route sat behind the
// header-only auth middleware, and a browser EventSource cannot set an
// Authorization header, so every connection 401'd. The /enterprise-nodes route
// authenticates from the `token` query parameter, which is what we send.
//
// The backend pipes lines from the Rust core through node-logs.service.ts, which
// parses each one and emits an enriched JSON object per `data:` frame:
//   { timestamp, level, message, type, bytes?, src?, dst?, client?, circuit_id?,
//     short, display }
// It also throttles to ~20 lines/sec, so we don't need to rate-limit here.
//
// Design notes:
//   • Only connects while the caller says so (`enabled`) — the modal opens the
//     stream when the Logs tab is shown and drops it on close, so we never hold
//     a socket per row in the table.
//   • Buffer is capped at MAX_LINES (ring, oldest dropped). A busy node emits
//     continuously; an unbounded array would grow without limit for as long as
//     the modal stays open.
//   • The backend answers 503 when the node is offline and 404 when it isn't the
//     company's. EventSource cannot read a status code — any failure surfaces as
//     onerror — so a drop before the first successful open is reported as
//     "unavailable" rather than retried forever. Once we HAVE opened, drops are
//     treated as transient and retried with capped exponential backoff.
// ─────────────────────────────────────────────────────────────────────────────
import { useState, useEffect, useRef, useCallback } from "react";
import { nodeLogStreamUrl } from "../../../services/nodesService";

const MAX_LINES         = 500;
const RECONNECT_BASE_MS = 1_000;
const RECONNECT_MAX_MS  = 30_000;

/**
 * @param {string|null} nodeId
 * @param {{ enabled?: boolean, level?: string }} opts
 * @returns {{
 *   lines: Array<object>,                       // enriched entries, oldest first
 *   status: "idle"|"connecting"|"open"|"unavailable",
 *   clear: () => void,
 * }}
 */
export function useNodeLogs(nodeId, { enabled = true, level } = {}) {
  const [lines,  setLines]  = useState([]);
  const [status, setStatus] = useState("idle");

  // Survives across reconnects within one subscription; reset when the effect
  // re-runs for a different node.
  const openedRef = useRef(false);

  const clear = useCallback(() => setLines([]), []);

  useEffect(() => {
    if (!enabled || !nodeId) { setStatus("idle"); return undefined; }

    let es         = null;
    let retryTimer = null;
    let attempts   = 0;
    let stopped    = false;

    openedRef.current = false;
    setLines([]);
    setStatus("connecting");

    const scheduleReconnect = () => {
      if (stopped) return;
      const delay = Math.min(RECONNECT_BASE_MS * 2 ** attempts, RECONNECT_MAX_MS);
      attempts += 1;
      retryTimer = setTimeout(connect, delay);
    };

    function connect() {
      if (stopped) return;

      // Read the token fresh on every attempt — it rotates on refresh, and a
      // cached URL would carry a dead one into the retry.
      const url = nodeLogStreamUrl(nodeId, { level });
      if (!url) { setStatus("unavailable"); return; }

      es = new EventSource(url);

      es.onopen = () => {
        openedRef.current = true;
        attempts = 0;                 // healthy connection — reset backoff
        setStatus("open");
      };

      es.onmessage = (e) => {
        let entry;
        try { entry = JSON.parse(e.data); } catch { return; } // keepalive / malformed
        setLines((prev) => {
          const next = [...prev, entry];
          return next.length > MAX_LINES ? next.slice(next.length - MAX_LINES) : next;
        });
      };

      es.onerror = (e) => {
        if (es) { es.close(); es = null; }
        // Two different things arrive here. A named `event: error` frame (which
        // the backend sends when the upstream node stream dies) carries `data`;
        // a transport-level drop does not. The former means the node itself is
        // gone, so reconnecting to it is pointless.
        if (e?.data) { setStatus("unavailable"); return; }
        if (!openedRef.current) {
          // Never opened: the node is offline (503), not ours (404), or the
          // token was rejected. Retrying won't change any of those — say so.
          setStatus("unavailable");
          return;
        }
        setStatus("connecting");
        scheduleReconnect();
      };
    }

    connect();

    return () => {
      stopped = true;
      if (retryTimer) { clearTimeout(retryTimer); retryTimer = null; }
      if (es) { es.close(); es = null; }
    };
  }, [nodeId, enabled, level]);

  return { lines, status, clear };
}
